"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectImageCarouselProps {
  images: string[];
  title: string;
  className?: string;
  autoplay?: boolean;
  autoplayInterval?: number;
}

export default function ProjectImageCarousel({
  images,
  title,
  className = "",
  autoplay = true,
  autoplayInterval = 4000,
}: ProjectImageCarouselProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoplayTimer = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    if (!autoplay || images.length <= 1 || isAutoplayPaused || isModalOpen) {
      return;
    }

    const startAutoplay = () => {
      autoplayTimer.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, autoplayInterval);
    };

    startAutoplay();

    return () => {
      if (autoplayTimer.current) {
        clearInterval(autoplayTimer.current);
        autoplayTimer.current = null;
      }
    };
  }, [autoplay, images.length, autoplayInterval, isAutoplayPaused, isModalOpen]);

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Modal functions
  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  // Hide header when modal is open
  useEffect(() => {
    const header = document.querySelector('header');
    if (header) {
      if (isModalOpen) {
        header.style.display = 'none';
      } else {
        header.style.display = '';
      }
    }
  }, [isModalOpen]);

  // Touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && images.length > 1) {
      nextImage();
    }
    if (isRightSwipe && images.length > 1) {
      prevImage();
    }
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      <div
        className="relative w-full h-[400px] sm:h-[500px] lg:h-[570px] rounded-lg overflow-hidden bg-white border border-gray-100 cursor-pointer group"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsAutoplayPaused(true)}
        onMouseLeave={() => setIsAutoplayPaused(false)}
      >
        <Image
          src={images[currentImageIndex]}
          alt={title}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-110"
        />

        {/* Zoom overlay */}
        <button
          onClick={() => openImageModal(images[currentImageIndex])}
          className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
          aria-label="Zoom image"
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors duration-200">
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="88" height="88" rx="44" fill="white" />
              <path
                d="M41.1992 43.7002H46.1992"
                stroke="#606977"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M43.6992 46.2002V41.2002"
                stroke="#606977"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M43.5 53C48.7467 53 53 48.7467 53 43.5C53 38.2533 48.7467 34 43.5 34C38.2533 34 34 38.2533 34 43.5C34 48.7467 38.2533 53 43.5 53Z"
                stroke="#606977"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M54 54L52 52"
                stroke="#606977"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>

        {/* Navigation Arrows - Only show on hover */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-200 z-20 opacity-0 group-hover:opacity-100"
              aria-label="Previous image"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-200 z-20 opacity-0 group-hover:opacity-100"
              aria-label="Next image"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}

        {/* Navigation Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-1 z-20 group-hover:scale-x-[-1] transition-transform duration-500">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentImageIndex
                    ? "w-6 h-3 bg-white"
                    : "w-3 h-3 bg-white opacity-50 hover:opacity-75"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {isModalOpen && selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeImageModal}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <motion.div
              className="relative bg-white rounded-xl p-3 max-w-[80vw] max-h-[80vh] flex flex-col shadow-2xl"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeImageModal}
                className="absolute -top-2 -right-2 text-white transition-colors p-2 bg-gray-800 hover:bg-gray-700 shadow-lg z-10 cursor-pointer rounded-full"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="relative flex-1 flex items-center justify-center overflow-hidden">
                <Image
                  src={selectedImage}
                  alt={title}
                  width={1400}
                  height={900}
                  className="object-contain max-w-full max-h-full"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
